import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tabIdToNomenclatureType, translateNomenclature } from '../../../utils/translateNomenclature';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { getConditionTypeCodeStyle } from '../../../utils/conditionTypeColor';
import './GenericNomenclatureList.css';

/**
 * Generic Nomenclature List Component
 * Reusable component for displaying and managing nomenclature items
 */
const GenericNomenclatureList = ({
  items,
  type,
  title,
  fields,
  mutations,
  onUpdate,
  categories
}) => {
  const isIpc = type === 'process-categories';
  const { t } = useTranslation(isIpc ? 'ipc' : 'common');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  // Get current user ID (replace with actual auth context)
  const getCurrentUserId = () => {
    return 'current-user-id'; // TODO: Replace with actual user ID from auth context
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      code: '',
      name: '',
      description: '',
      isActive: true,
      displayOrder: items.length + 1,
      ...fields.reduce((acc, field) => {
        if (field.type === 'boolean') acc[field.name] = false;
        else if (field.type === 'number') acc[field.name] = 0;
        else acc[field.name] = '';
        return acc;
      }, {})
    });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ ...item });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const userId = getCurrentUserId();

      if (isAdding) {
        await mutations.create(formData, userId);
      } else {
        await mutations.update(editingId, formData, userId);
      }

      setIsAdding(false);
      setEditingId(null);
      setFormData({});
      onUpdate();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);
      const userId = getCurrentUserId();
      console.log('Deleting item:', id, 'with user:', userId);
      await mutations.remove(id, userId);
      console.log('Delete successful, calling onUpdate');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderField = (field, value) => {
    switch (field.type) {
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleChange(field.name, e.target.checked)}
            disabled={loading}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || 0}
            onChange={(e) => handleChange(field.name, parseInt(e.target.value))}
            disabled={loading}
            className="field-input"
          />
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={loading}
            className="field-input"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options && field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'color':
        return (
          <input
            type="color"
            value={value && value.trim() ? value : '#999999'}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={loading}
            className="field-color"
            title={value ? value : 'No color selected'}
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            disabled={loading}
            className="field-input"
            placeholder={field.placeholder || ''}
          />
        );
    }
  };

  const renderRow = (item, isEditing) => {
    const data = isEditing ? formData : item;

    return (
      <tr key={item?.id || 'new'} className={!data.isActive ? 'inactive-row' : ''}>
        <td>
          {isEditing ? (
            <input
              type="text"
              value={data.code || ''}
              onChange={(e) => handleChange('code', e.target.value)}
              disabled={loading}
              className="field-input"
              placeholder={isIpc ? t('code') : 'Code'}
            />
          ) : type === 'condition-types' && item.color && item.color.trim() ? (
            <span className="code-badge" style={getConditionTypeCodeStyle(item.color)}>
              {item.code}
            </span>
          ) : (
            <span className="code-badge">{item.code}</span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="text"
              value={data.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={loading}
              className="field-input"
              placeholder="Name"
            />
          ) : (
            translateNomenclature(t, tabIdToNomenclatureType(type), item)
          )}
        </td>
        <td>
          {isEditing ? (
            <textarea
              value={data.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
              className="field-textarea"
              placeholder={isIpc ? t('description') : 'Description'}
              rows="2"
            />
          ) : (
            <span className="description-text">{item.description || '-'}</span>
          )}
        </td>
        {fields.map(field => (
          <td key={field.name}>
            {isEditing ? (
              renderField(field, data[field.name])
            ) : (
              <span>
                {field.type === 'boolean'
                  ? (item[field.name] ? 'Yes' : 'No')
                  : field.type === 'color'
                  ? (item[field.name] ? (
                      <span className="color-preview" style={{ backgroundColor: item[field.name], minWidth: 24, height: 20, display: 'inline-block', borderRadius: 4 }} title={item[field.name]} />
                    ) : (
                      <span className="color-none">No color</span>
                    ))
                  : field.name === 'categoryId' && categories
                  ? (categories.find(cat => cat.code === item[field.name])?.name || item[field.name] || '-')
                  : item[field.name] || '-'}
              </span>
            )}
          </td>
        ))}
        <td>
          {isEditing ? (
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                checked={data.isActive || false}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                disabled={loading}
              />
              <span>{isIpc ? t('active') : 'Active'}</span>
            </div>
          ) : (
            <span className={`status-badge ${item.isActive ? 'active' : 'inactive'}`}>
              {item.isActive ? (isIpc ? t('active') : 'Active') : (isIpc ? t('inactive') : 'Inactive')}
            </span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="number"
              value={data.displayOrder || 0}
              onChange={(e) => handleChange('displayOrder', parseInt(e.target.value))}
              disabled={loading}
              className="field-input small"
            />
          ) : (
            item.displayOrder
          )}
        </td>
        <td>
          <div className="action-buttons">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="btn btn-save btn-icon"
                  title={isIpc ? t('save') : 'Save'}
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="btn btn-cancel btn-icon"
                  title={isIpc ? t('cancel') : 'Cancel'}
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleEdit(item)}
                  disabled={loading}
                  className="btn btn-edit btn-icon"
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  disabled={loading}
                  className="btn btn-delete btn-icon"
                  title={isIpc ? t('delete') : 'Delete'}
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="nomenclature-list">
      <div className="list-header">
        <div>
          <h2>{title}</h2>
          <p className="item-count">{isIpc ? t('itemsCount', { count: items.length }) : `${items.length} items`}</p>
        </div>
        <button
          onClick={handleAdd}
          disabled={isAdding || loading}
          className="btn btn-primary"
        >
          <Plus size={16} />
          {isIpc ? t('addNew') : 'Add New'}
        </button>
      </div>

      <div className="table-wrapper">
        <table className="nomenclature-table">
          <thead>
            <tr>
              <th>{isIpc ? t('code') : 'Code'}</th>
              <th>{isIpc ? t('name') : 'Name'}</th>
              <th>{isIpc ? t('description') : 'Description'}</th>
              {fields.map(field => (
                <th key={field.name}>{field.label}</th>
              ))}
              <th>{isIpc ? t('status') : 'Status'}</th>
              <th>{isIpc ? t('order') : 'Order'}</th>
              <th>{isIpc ? t('actions') : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {isAdding && renderRow(null, true)}
            {items.map(item => 
              renderRow(item, editingId === item.id)
            )}
          </tbody>
        </table>
      </div>

      {items.length === 0 && !isAdding && (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <p>{isIpc ? t('noItemsFound') : 'No items found'}</p>
          <button onClick={handleAdd} className="btn btn-primary">
            <Plus size={16} />
            {isIpc ? t('addFirstItem') : 'Add First Item'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GenericNomenclatureList;

